// Contratos Zod del módulo de Mantenimiento (CMMS) — batch 1 (núcleo operable).
//
// Portados del CMMS standalone y ADAPTADOS al esquema real de las migraciones
// 20260705000066/67 (fusión):
//   - `activos` cuelga de la jerarquía real: planta_id obligatorio + linea_id
//     opcional (decisión D3 del informe). NO existe `criticidad` en la fusión.
//   - `ordenes_trabajo` de la fusión NO tiene prioridad / asignado_a /
//     orden_trabajo_padre_id (capa de campo del rediseño → batch 2).
//   - Autorización de dos gates (informe §G): VER = capacidad
//     `puede_ver_mantenimiento` (tenant_users); OPERAR = rol de módulo en
//     `usuarios_mantenimiento` (coordinador_mtto / tecnico / almacen).
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Enums del dominio (espejo de los tipos de la migración 067)
// ---------------------------------------------------------------------------
export const rolMantenimientoSchema = z.enum(['coordinador_mtto', 'tecnico', 'almacen']);
export const tipoActivoMttoSchema = z.enum([
    'sitio',
    'area',
    'linea',
    'equipo',
    'proceso',
    'componente',
]);
export const estadoOtSchema = z.enum([
    'abierta',
    'programada',
    'en_proceso',
    'cerrada',
    'cancelada',
]);
// El enum de BD `tipo_origen_ot` tiene CUATRO valores; el cuarto es 'paro'.
// Hay que separar el dominio de ESCRITURA del de LECTURA porque no coinciden:
//
//   · Escritura (`origenOtSchema`): lo que un usuario puede pedir al crear una
//     OT a mano. 'paro' NO entra — nadie teclea "esta orden nació de un paro";
//     eso lo decide el sistema.
//   · Lectura (`origenOtLecturaSchema`): lo que la BD puede devolver. Tiene que
//     incluir 'paro' porque `POST /api/mantenimiento/paros/:id/orden`
//     (mantenimiento_paros.ts) crea órdenes con ese origen. Sin él, la lista de
//     órdenes de trabajo se valida con `z.array(...)` y falla ENTERA —
//     "No pudimos cargar", no una fila mal pintada— en cuanto existe una sola
//     orden nacida de un paro.
export const origenOtSchema = z.enum(['plan', 'manual', 'solicitud']);
export const origenOtLecturaSchema = z.enum(['plan', 'manual', 'solicitud', 'paro']);
export const tipoMovimientoRepuestoSchema = z.enum(['entrada', 'salida', 'ajuste']);
// ---------------------------------------------------------------------------
// Activos
// ---------------------------------------------------------------------------
export const activoMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    parent_id: z.string().uuid().nullable(),
    tipo: tipoActivoMttoSchema,
    codigo: z.string().nullable(),
    nombre: z.string(),
    ubicacion: z.string().nullable(),
    planta_id: z.string().uuid(),
    linea_id: z.string().uuid().nullable(),
    // Resueltos vía JOIN en el backend (no son columnas de `activos`)
    planta_nombre: z.string().nullable(),
    linea_nombre: z.string().nullable(),
    estado: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const activoMttoCreateSchema = z.object({
    parent_id: z.string().uuid().optional(),
    tipo: tipoActivoMttoSchema,
    codigo: z.string().max(40).optional(),
    nombre: z.string().min(1).max(120),
    ubicacion: z.string().max(200).optional(),
    planta_id: z.string().uuid(),
    linea_id: z.string().uuid().optional(),
});
export const activoMttoUpdateSchema = z
    .object({
    parent_id: z.string().uuid().nullable().optional(),
    tipo: tipoActivoMttoSchema.optional(),
    codigo: z.string().max(40).optional(),
    nombre: z.string().min(1).max(120).optional(),
    ubicacion: z.string().max(200).optional(),
    planta_id: z.string().uuid().optional(),
    // `null` explícito desvincula el activo de la línea (queda a nivel planta)
    linea_id: z.string().uuid().nullable().optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'Debe enviar al menos un campo' });
// ---------------------------------------------------------------------------
// Órdenes de trabajo
// ---------------------------------------------------------------------------
export const ordenTrabajoMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    codigo: z.string().nullable(),
    activo_id: z.string().uuid(),
    activo_nombre: z.string().nullable(),
    // Lectura: acepta también 'paro' (ver el comentario de origenOtLecturaSchema).
    origen: origenOtLecturaSchema,
    estado: estadoOtSchema,
    descripcion: z.string(),
    tipo_mantenimiento: z.string().nullable(),
    fecha_programada: z.string().nullable(),
    abierta_at: z.string(),
    programada_at: z.string().nullable(),
    iniciada_at: z.string().nullable(),
    cerrada_at: z.string().nullable(),
    horas_reales: z.string().nullable(),
    cerrada_por: z.string().uuid().nullable(),
    notas_cierre: z.string().nullable(),
    estado_registro: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});
export const ordenTrabajoMttoCreateSchema = z.object({
    activo_id: z.string().uuid(),
    origen: origenOtSchema,
    descripcion: z.string().min(1).max(500),
    tipo_mantenimiento: z.string().max(60).optional(),
    fecha_programada: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const ordenTrabajoMttoProgramarSchema = z.object({
    fecha_programada: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const ordenTrabajoMttoCerrarSchema = z.object({
    horas_reales: z.coerce.number().nonnegative().optional(),
    notas_cierre: z.string().max(1000).optional(),
});
// `razon` obligatoria: cancelar una OT SIEMPRE debe justificarse (auditoría).
export const ordenTrabajoMttoCancelarSchema = z.object({
    razon: z.string().min(1).max(500),
});
export const otTareaMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    orden_trabajo_id: z.string().uuid(),
    descripcion: z.string(),
    est_min: z.number().int(),
    hecha: z.boolean(),
    orden: z.number().int(),
});
export const otTareaMttoCreateSchema = z.object({
    descripcion: z.string().min(1).max(300),
    est_min: z.number().int().min(0).default(0),
    orden: z.number().int().min(0).default(0),
});
export const otRepuestoMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    orden_trabajo_id: z.string().uuid(),
    repuesto_id: z.string().uuid(),
    cantidad: z.string(),
});
export const otRepuestoMttoCreateSchema = z.object({
    repuesto_id: z.string().uuid(),
    cantidad: z.coerce.number().positive().default(1),
});
export const otEjecutorMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    orden_trabajo_id: z.string().uuid(),
    usuario_id: z.string().uuid().nullable(),
    nombre: z.string(),
    cargo: z.string().nullable(),
});
export const otEjecutorMttoCreateSchema = z.object({
    usuario_id: z.string().uuid().optional(),
    nombre: z.string().min(1).max(120),
    cargo: z.string().max(80).optional(),
});
export const ordenTrabajoMttoDetailSchema = ordenTrabajoMttoSchema.extend({
    tareas: otTareaMttoSchema.array(),
    repuestos: otRepuestoMttoSchema.array(),
    ejecutores: otEjecutorMttoSchema.array(),
});
// ---------------------------------------------------------------------------
// Repuestos + movimientos de stock
// ---------------------------------------------------------------------------
export const repuestoMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    codigo: z.string().nullable(),
    descripcion: z.string(),
    unidad: z.string().nullable(),
    existencia: z.string(),
    min_cant: z.string().nullable(),
    max_cant: z.string().nullable(),
    costo: z.string(),
    ubicacion: z.string().nullable(),
    proveedor: z.string().nullable(),
    sku: z.string().nullable(),
    referencia: z.string().nullable(),
    estado: z.string().nullable(),
    detalle: z.string().nullable(),
    estado_registro: z.string(),
    created_at: z.string(),
    updated_at: z.string(),
});
// Los formularios web envían números como string; coercionamos aquí. Los
// opcionales tratan "" como ausencia (undefined) en vez de coercionar a 0.
const optionalNum = z.preprocess((v) => (v === '' || v === null || v === undefined ? undefined : v), z.coerce.number().min(0).optional());
export const repuestoMttoCreateSchema = z.object({
    codigo: z.string().max(40).optional(),
    descripcion: z.string().min(1).max(300),
    unidad: z.string().max(30).optional(),
    existencia: z.coerce.number().min(0).default(0),
    min_cant: optionalNum,
    max_cant: optionalNum,
    costo: z.coerce.number().min(0).default(0),
    ubicacion: z.string().max(200).optional(),
    proveedor: z.string().max(200).optional(),
    sku: z.string().max(80).optional(),
    referencia: z.string().max(80).optional(),
    estado: z.string().max(60).optional(),
    detalle: z.string().optional(),
});
export const repuestoMttoUpdateSchema = z
    .object({
    codigo: z.string().max(40).optional(),
    descripcion: z.string().min(1).max(300).optional(),
    unidad: z.string().max(30).optional(),
    existencia: optionalNum,
    min_cant: optionalNum,
    max_cant: optionalNum,
    costo: optionalNum,
    ubicacion: z.string().max(200).optional(),
    proveedor: z.string().max(200).optional(),
    sku: z.string().max(80).optional(),
    referencia: z.string().max(80).optional(),
    estado: z.string().max(60).optional(),
    detalle: z.string().optional(),
})
    .refine((o) => Object.keys(o).length > 0, { message: 'Debe enviar al menos un campo' });
// POST /repuestos/:id/movimientos — el repuesto_id viaja en el path.
export const movimientoRepuestoMttoCreateSchema = z.object({
    tipo: tipoMovimientoRepuestoSchema,
    cantidad: z.coerce.number().positive(),
    activo_id: z.string().uuid().optional(),
    orden_trabajo_id: z.string().uuid().optional(),
    notas: z.string().max(500).optional(),
});
export const movimientoRepuestoMttoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    repuesto_id: z.string().uuid(),
    tipo: tipoMovimientoRepuestoSchema,
    cantidad: z.string(),
    activo_id: z.string().uuid().nullable(),
    orden_trabajo_id: z.string().uuid().nullable(),
    notas: z.string().nullable(),
    movido_at: z.string(),
});
// ---------------------------------------------------------------------------
// KPIs del dashboard del módulo
// ---------------------------------------------------------------------------
export const mantenimientoKpisSchema = z.object({
    ot_por_estado: z.object({
        abierta: z.number().int(),
        programada: z.number().int(),
        en_proceso: z.number().int(),
        cerrada: z.number().int(),
        cancelada: z.number().int(),
    }),
    ot_vencidas: z.number().int(),
    activos_total: z.number().int(),
    repuestos_bajo_minimo: z.number().int(),
    activos_por_planta: z
        .object({
        planta_id: z.string().uuid(),
        planta_nombre: z.string(),
        activos: z.number().int(),
    })
        .array(),
});
// ---------------------------------------------------------------------------
// Informes
// ---------------------------------------------------------------------------
// GET /informes/paros — contra la tabla REAL de producción `registros_paro`
// (paros hora-a-hora capturados por líderes), agregada por causal madre y línea.
export const informeParosResponseSchema = z.object({
    desde: z.string(),
    hasta: z.string(),
    total_paros: z.number().int(),
    total_minutos: z.number(),
    por_causal: z
        .object({
        causal_madre_id: z.string().uuid(),
        causal_madre_nombre: z.string(),
        paros: z.number().int(),
        minutos: z.number(),
    })
        .array(),
    por_linea: z
        .object({
        linea_id: z.string().uuid(),
        linea_nombre: z.string(),
        paros: z.number().int(),
        minutos: z.number(),
    })
        .array(),
});
// ---------------------------------------------------------------------------
// GET /paros/analisis — análisis detallado de paros para el coordinador de
// mantenimiento. Visual paralela a C8 (coordinador de operación) pero con:
//   - paros ABIERTOS visibles (C8 solo muestra cerrados),
//   - $ perdido (unidades no producidas × precio vigente) si puede_ver_costos,
//   - filtro por categoría de mantenimiento (por catálogo, no hardcode).
// Los campos de dinero (`unidades`, `valor_cop`, `unidades_no_producidas`) se
// OMITEN cuando `con_costos=false` — nunca un 0 engañoso.
// ---------------------------------------------------------------------------
/** Query param booleano: acepta true/false como string (query) o boolean. */
const boolQueryParam = (def) => z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .default(def)
    .transform((v) => v === true || v === 'true');
export const estadoParoFiltroSchema = z.enum(['cerrados', 'abiertos', 'todos']);
export const analisisParosMttoQuerySchema = z.object({
    /** Default: semana operativa actual (inicio_semana_operativa → +6). */
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
    planta_id: z.string().uuid().optional(),
    linea_id: z.string().uuid().optional(),
    /** Filtro explícito por categoría; tiene prioridad sobre solo_mantenimiento. */
    categoria_id: z.string().uuid().optional(),
    causal_madre_id: z.string().uuid().optional(),
    /** false (default) = solo causales que afectan disponibilidad (excluye programados). */
    incluir_programados: boolQueryParam(false),
    /** true (default) = solo la categoría de mantenimiento del catálogo del tenant. */
    solo_mantenimiento: boolQueryParam(true),
    /** A diferencia de C8, el coordinador de mtto VE los paros abiertos. */
    estado_paro: estadoParoFiltroSchema.default('todos'),
    /** Bucket de la tendencia: fecha o semana operativa. */
    granularidad: z.enum(['dia', 'semana']).default('dia'),
});
const categoriaParoRefSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    color: z.string().nullable(),
});
export const analisisParoCausalMadreMttoSchema = z.object({
    causal_madre_id: z.string().uuid(),
    nombre: z.string(),
    categoria: categoriaParoRefSchema,
    paros_count: z.number().int().min(0),
    /** Suma de minutos de paros CERRADOS (los abiertos aún no tienen duración). */
    minutos: z.number().min(0),
    pct_minutos: z.number().min(0).max(100),
    unidades: z.number().optional(),
    valor_cop: z.number().optional(),
});
export const analisisParoCausalEspecificaMttoSchema = analisisParoCausalMadreMttoSchema.extend({
    causal_id: z.string().uuid(),
    /** True si el nombre contiene "otro" — flag de calidad de dato (como C8). */
    es_otro: z.boolean(),
});
export const analisisParoTendenciaMttoSchema = z.object({
    /** 'YYYY-MM-DD' (granularidad dia) o 'IYYY-Wxx' operativa (semana). */
    bucket: z.string(),
    causal_madre_id: z.string().uuid(),
    nombre: z.string(),
    color: z.string().nullable(),
    minutos: z.number().min(0),
    valor_cop: z.number().optional(),
});
export const analisisParoTurnoMttoSchema = z.object({
    turno_codigo: z.string(),
    paros_count: z.number().int().min(0),
    minutos: z.number().min(0),
    valor_cop: z.number().optional(),
});
export const analisisParoHoraMttoSchema = z.object({
    /** Hora 0-23 en pared local del tenant (hora_inicio real del paro). */
    hora: z.number().int().min(0).max(23),
    paros_count: z.number().int().min(0),
    minutos: z.number().min(0),
});
export const analisisParoLineaMttoSchema = z.object({
    linea_id: z.string().uuid(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    paros_count: z.number().int().min(0),
    minutos: z.number().min(0),
    valor_cop: z.number().optional(),
    /** La línea tiene al menos un paro sin cerrar dentro del filtro aplicado. */
    paro_activo: z.boolean(),
});
export const analisisParosMttoResponseSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    granularidad: z.enum(['dia', 'semana']),
    /** Capability puede_ver_costos resuelta para el actor; gobierna los campos $. */
    con_costos: z.boolean(),
    /**
     * True si se aplicó el filtro solo_mantenimiento (existe la categoría
     * INTERVENCION_MANTENIMIENTO en el catálogo del tenant). False = se devolvió
     * todo (catálogo sin esa categoría, o filtro apagado/reemplazado).
     */
    filtro_mantenimiento_aplicado: z.boolean(),
    totales: z.object({
        paros_count: z.number().int().min(0),
        minutos_totales: z.number().min(0),
        paros_abiertos: z.number().int().min(0),
        /** Minutos transcurridos de los paros aún abiertos (corte: ahora, hora tenant). */
        minutos_paros_abiertos: z.number().min(0),
        /** SKUs del rango con paros pero sin precio vigente ($0 — calidad de dato). */
        skus_sin_precio: z.number().int().min(0),
        unidades_no_producidas: z.number().optional(),
        valor_cop: z.number().optional(),
    }),
    por_causal_madre: analisisParoCausalMadreMttoSchema.array(),
    por_causal_especifica: analisisParoCausalEspecificaMttoSchema.array(),
    tendencia: analisisParoTendenciaMttoSchema.array(),
    por_turno: analisisParoTurnoMttoSchema.array(),
    perfil_horario: analisisParoHoraMttoSchema.array(),
    por_linea: analisisParoLineaMttoSchema.array(),
});
export const informeOtResumenSchema = z.object({
    total: z.number().int(),
    por_estado: z.object({ estado: estadoOtSchema, cantidad: z.number().int() }).array(),
    por_tipo: z.object({ tipo_mantenimiento: z.string(), cantidad: z.number().int() }).array(),
    proporcion_prevencion: z.number(),
});
// MTTR = promedio de horas entre abierta_at y cerrada_at de OT correctivas
// cerradas (ciclo completo apertura → cierre).
export const informeSlaSchema = z.object({
    muestras: z.number().int(),
    mttr_horas: z.number(),
});
// ---------------------------------------------------------------------------
// Estructura (catálogo plantas → líneas para los selects encadenados del FE).
// Fuente PROPIA del módulo: cualquier usuario que puede VER mantenimiento la
// consulta, sin depender de endpoints de coordinador/admin (que un usuario
// dedicado con rol base 'lider' no puede tocar).
// ---------------------------------------------------------------------------
export const estructuraMttoResponseSchema = z.object({
    plantas: z
        .object({
        id: z.string().uuid(),
        nombre: z.string(),
    })
        .array(),
    lineas: z
        .object({
        id: z.string().uuid(),
        nombre: z.string(),
        planta_id: z.string().uuid(),
    })
        .array(),
});
// ---------------------------------------------------------------------------
// Usuarios del módulo (usuarios_mantenimiento)
// ---------------------------------------------------------------------------
export const usuarioModuloMttoSchema = z.object({
    id: z.string().uuid(),
    user_id: z.string().uuid(),
    email: z.string(),
    nombre: z.string(),
    apellido: z.string(),
    rol_mtto: rolMantenimientoSchema,
    planta_id: z.string().uuid().nullable(),
    planta_nombre: z.string().nullable(),
    activo: z.boolean(),
    created_at: z.string(),
    updated_at: z.string(),
});
// PUT /usuarios-modulo — upsert de la asignación (tenant_id, user_id, rol_mtto).
// `activo: false` revoca sin borrar (soft-revoke); planta_id NULL = todas.
export const usuarioModuloMttoUpsertSchema = z.object({
    user_id: z.string().uuid(),
    rol_mtto: rolMantenimientoSchema,
    planta_id: z.string().uuid().nullable().optional(),
    activo: z.boolean().default(true),
});
// ---------------------------------------------------------------------------
// GET /personal/kpis — rendimiento del personal del módulo (pestaña Personal).
// KPIs "gratis" (derivan de validaciones_paro y registros_paro). Los campos
// mttr/mtbf/preventivos quedan NULLABLES a propósito: son la estructura para
// los indicadores que el cliente medirá (Excel INDICADORES: MTTR, MTBF,
// % mtto programado, cumplimiento preventivo) — se llenan en una fase futura.
// ---------------------------------------------------------------------------
export const personalMttoKpiSchema = z.object({
    user_id: z.string().uuid(),
    paros_validados: z.number().int().min(0),
    paros_cerrados: z.number().int().min(0),
    correcciones_propuestas: z.number().int().min(0),
    correcciones_aprobadas: z.number().int().min(0),
    correcciones_rechazadas: z.number().int().min(0),
    mttr_horas: z.number().nullable(),
    mtbf_horas: z.number().nullable(),
    pct_preventivos: z.number().nullable(),
});
export const personalMttoKpisQuerySchema = z.object({
    /** Default: últimos 30 días hasta el día operativo actual. */
    desde: z.string().date().optional(),
    hasta: z.string().date().optional(),
});
export const personalMttoKpisResponseSchema = z.object({
    rango: z.object({ desde: z.string().date(), hasta: z.string().date() }),
    items: personalMttoKpiSchema.array(),
});
//# sourceMappingURL=mantenimiento.js.map